/**
 * Priority Rank Reorder — drag priorities to change rank directly.
 *
 * Mirrors the existing @dnd-kit reorder pattern used for item-pool ranking
 * (src/components/prioritization/spaces/ItemPoolEditor.tsx — since N-16 drawn
 * by ListTable's ranked mode, which renders every row in flow so the drag
 * works) rather than inventing a new drag UX. Deliberately NOT built into the main
 * Priorities table: that table is virtualized (ListTable.tsx /
 * @tanstack/react-virtual), and @dnd-kit's sortable context expects every
 * sortable item mounted in the DOM at once — the two don't mix without a
 * much larger change to the shared table primitive every other entity list
 * also depends on. This is a focused, non-virtualized list over exactly the
 * priorities currently visible under the active Input Scope, so dragging
 * always operates on the same set the rank numbers on screen mean.
 *
 * Writes through reorderPriorities, the same renumbering function the Rank
 * number-input in PriorityDetailPanel uses — one correct implementation,
 * two entry points.
 */

import { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Loader2, X } from 'lucide-react';
import { reorderPriorities } from '../../../lib/planningEngineService';
import type { Priority } from '../../../lib/planningEngineModule.types';

function sortByRank(list: Priority[]): Priority[] {
  return [...list].sort(
    (a, b) => (a.forceRank ?? Number.MAX_SAFE_INTEGER) - (b.forceRank ?? Number.MAX_SAFE_INTEGER),
  );
}

function ReorderRow({ priority, rank }: { priority: Priority; rank: number }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: priority.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 px-3 py-2.5 border-b border-[var(--rule)] dark:border-[var(--rule)] transition-colors ${
        isDragging ? 'opacity-50 bg-[var(--surface)] dark:bg-[var(--surface)]' : 'hover:bg-[var(--surface)]/60 dark:hover:bg-[var(--hover-tint)]'
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        className="flex-shrink-0 text-[var(--text-ter)] hover:text-[var(--text-ter)] cursor-grab active:cursor-grabbing"
        tabIndex={-1}
        aria-label={`Drag to reorder ${priority.name}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold tabular-nums bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)]">
        {rank}
      </span>
      <span className="flex-1 min-w-0 text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate" title={priority.name}>
        {priority.name}
      </span>
      {priority.priorityResourceType && (
        <span className="flex-shrink-0 text-xs text-[var(--text-ter)] capitalize">{priority.priorityResourceType}</span>
      )}
    </div>
  );
}

export function PriorityRankReorderModal({
  priorities,
  onClose,
  onReordered,
}: {
  priorities: Priority[];
  onClose: () => void;
  onReordered: (updated: Priority[]) => void;
}) {
  const [order, setOrder] = useState<Priority[]>(() => sortByRank(priorities));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep in sync if the caller's list changes underneath (e.g. a background
  // reload) while the modal is open, without discarding an in-flight drag.
  useEffect(() => {
    if (!saving) setOrder(sortByRank(priorities));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorities]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.findIndex((p) => p.id === active.id);
    const newIndex = order.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(order, oldIndex, newIndex);
    setOrder(newOrder);
    setSaving(true);
    setError(null);
    try {
      const updated = await reorderPriorities(newOrder);
      setOrder(sortByRank(updated));
      onReordered(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setOrder(sortByRank(priorities)); // revert to last known-good on failure
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)]/80 dark:border-[var(--rule)] w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)] dark:border-[var(--rule)] flex-shrink-0">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Reorder priorities</h3>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
              Drag to change rank — everyone in between shifts to match.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin text-[var(--text-ter)]" />}
            <button type="button" onClick={onClose} className="p-1 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]">
              <X className="h-5 w-5 text-[var(--text-ter)]" />
            </button>
          </div>
        </div>

        {error && (
          <div className="px-6 py-2 text-sm text-[var(--coral)] dark:text-[var(--coral)] border-b border-[var(--rule)] dark:border-[var(--rule)]">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {order.length === 0 ? (
            <p className="px-6 py-8 text-sm text-[var(--text-ter)] text-center">No priorities in the current scope.</p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleDragEnd(e)}>
              <SortableContext items={order.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                {order.map((p, i) => (
                  <ReorderRow key={p.id} priority={p} rank={i + 1} />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>
    </div>
  );
}
